import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, inject, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/slide-toggle';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import {
    DADATA_API_KEY,
    DADATA_COUNTRY_SCOPE,
    GOOGLE_MAPS_API_KEY,
    GOOGLE_PLACES_COUNTRY_SCOPE,
    PELIAS_API_BASE_URL,
} from '@natkor/ng-shared';
import { routes } from './app.routes';
import { ADMIN_BFF_API_BASE_URL } from './core/api/admin-bff-api.client';
import { AI_AGENT_ADMIN_API_BASE_URL } from './core/api/ai-agent-admin-api.client';
import { ANALYTICS_ADMIN_API_BASE_URL } from './core/api/analytics-admin-api.client';
import { CHAT_ADMIN_API_BASE_URL } from './core/api/chat-admin-api.client';
import { DVS_ADMIN_API_BASE_URL } from './core/api/dvs-admin-api.client';
import { GEO_ADMIN_API_BASE_URL } from './core/api/geo-admin-api.client';
import { LOCALIZATION_ADMIN_API_BASE_URL } from './core/api/localization-admin-api.client';
import { MONOLITH_ADMIN_API_BASE_URL } from './core/api/monolith-admin-api.client';
import { NOTIFICATIONS_ADMIN_API_BASE_URL } from './core/api/notifications-admin-api.client';
import { SEO_ADMIN_API_BASE_URL } from './core/api/seo-admin-api.client';
import { STATS_ADMIN_API_BASE_URL } from './core/api/stats-admin-api.client';
import { SUPPORT_KNOWLEDGE_PUBLIC_API_BASE_URL } from './core/api/support-knowledge-public-api.client';
import { AUTH_ADMIN_API_BASE_URL } from './core/auth/auth-admin-api.client';
import { AUTH_ADMIN_CUSTOMERS_API_BASE_URL } from './core/auth/auth-admin-customers-api.client';
import { provideIdentityService } from './core/auth/provide-identity';
import { CountryDomainService } from './core/country/country-domain.service';
import { CountryCode } from './core/country/country.model';
import { provideCountryAccess } from './core/country/provide-country-access';
import { NavigationHistoryService } from './core/navigation/navigation-history.service';
import { apiErrorInterceptor } from './infrastructure/interceptors/api-error.interceptor';
import { API_BASE_URL_PLACEHOLDER, countryDomainBaseUrlInterceptor } from './infrastructure/interceptors/country-domain-base-url.interceptor';
import { jwtInterceptor } from './infrastructure/interceptors/jwt.interceptor';

/**
 * STAFF auth API — the shared staff-auth service (`AuthMode.Admin`), whose database holds our own
 * staff accounts and which is NOT per-country. It is reached same-origin through the admin web
 * ingress, which routes `/api/auth/*` to that service and strips the prefix so it sees `/admin/*`.
 *
 * Only staff-account operations (login, token refresh, user/role management) belong here. Anything
 * that reads or writes a CUSTOMER's auth account must go to that country's Auth instead — see
 * AUTH_ADMIN_CUSTOMERS_API_PATH below.
 */
const AUTH_ADMIN_API_PATH = '/api/auth';

/**
 * AdminBff is the single API door for all per-country admin APIs (see docs/architecture.md — API
 * access model). It is reached through the active country's API origin: the country-domain
 * interceptor rewrites the placeholder to `https://api.<countryDomain>`, and each country's
 * nanny-backend ingress routes `/admin-bff/*` on that host to the BFF and strips the prefix, so
 * the BFF serves its native routes. The staff JWT is attached by the jwtInterceptor.
 */
const ADMIN_BFF_API_PATH = `${API_BASE_URL_PLACEHOLDER}/admin-bff`;

/**
 * Per-service admin APIs go through the BFF's transparent passthrough routes: the BFF
 * authenticates the staff JWT, strips the `/admin-bff/<service>` prefix and forwards the request
 * (with the original Authorization header) to the owning service, which sees the same native
 * paths the generated clients produce (e.g. `/admin/*` for the Monolith, `/admin-ai-agent/*` for
 * AiAgent, `/public/*` for SupportKnowledge — SK is deployed per region, and routing through the
 * active country's API origin lands in the right region's instance).
 *
 * `auth` is the ACTIVE COUNTRY's Auth instance (`AuthMode.Web`), whose database holds that country's
 * customer accounts. Magic login, admin password reset and linked login providers all operate on a
 * customer, so they belong here and not on the staff-auth service above — which would look the
 * customer up in the staff account database.
 */
const MONOLITH_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/monolith`;
const AUTH_ADMIN_CUSTOMERS_API_PATH = `${ADMIN_BFF_API_PATH}/auth`;
const AI_AGENT_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/ai-agent`;
const SUPPORT_KNOWLEDGE_API_PATH = `${ADMIN_BFF_API_PATH}/support-knowledge`;
const GEO_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/geo`;
const NOTIFICATIONS_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/notifications`;
const SEO_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/seo`;
const CHAT_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/chat`;
const STATS_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/stats`;
const ANALYTICS_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/analytics`;
const DVS_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/dvs`;
const LOCALIZATION_ADMIN_API_PATH = `${ADMIN_BFF_API_PATH}/localization`;

/**
 * DaData suggestions token for the address editor's RU-family lookup. A public browser key — the
 * same one the Web client ships in its app-config — calling DaData directly from the browser via
 * `HttpBackend`, so no staff JWT or interceptor rewriting is involved.
 */
const DADATA_SUGGESTIONS_API_KEY = 'a71361f3006b1a6154e7352ba288906e491db434';

/**
 * Google Places browser key for the address editor's CIS-country lookup (KZ/KG/MD/UZ/TJ). Loads the
 * Maps JS API (with the `places` library) directly in the browser; the key must have the Maps
 * JavaScript API, Places API and Geocoding API enabled. Suggestions are country-scoped per request
 * (the country-scope factory re-reads the active country, covering runtime country switches).
 */
const GOOGLE_PLACES_API_KEY = 'AIzaSyBzRb7hV7_LUQxZafcOkWi_QXkmVWkxP8Q';

export const appConfig: ApplicationConfig = {
    providers: [
        provideBrowserGlobalErrorListeners(),
        provideZonelessChangeDetection(),
        provideRouter(routes, withComponentInputBinding()),
        provideHttpClient(withInterceptors([countryDomainBaseUrlInterceptor, jwtInterceptor, apiErrorInterceptor])),
        provideAnimationsAsync(),
        provideIdentityService(),
        // Sits next to the identity provider: it is what narrows CountryDomainService to the countries
        // the signed-in staff member's token is assigned to.
        provideCountryAccess(),
        // Root services are created on first injection; this one must be listening from the first
        // navigation, or the list URL a detail page's back control asks for was never recorded.
        provideAppInitializer(() => {
            inject(NavigationHistoryService);
        }),
        // Design toggles are plain round thumbs — no selected/unselected glyph (see styles.scss sizing).
        { provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS, useValue: { hideIcon: true } },
        { provide: MONOLITH_ADMIN_API_BASE_URL, useValue: MONOLITH_ADMIN_API_PATH },
        { provide: AUTH_ADMIN_API_BASE_URL, useValue: AUTH_ADMIN_API_PATH },
        { provide: AUTH_ADMIN_CUSTOMERS_API_BASE_URL, useValue: AUTH_ADMIN_CUSTOMERS_API_PATH },
        { provide: SUPPORT_KNOWLEDGE_PUBLIC_API_BASE_URL, useValue: SUPPORT_KNOWLEDGE_API_PATH },
        { provide: ADMIN_BFF_API_BASE_URL, useValue: ADMIN_BFF_API_PATH },
        { provide: AI_AGENT_ADMIN_API_BASE_URL, useValue: AI_AGENT_ADMIN_API_PATH },
        { provide: GEO_ADMIN_API_BASE_URL, useValue: GEO_ADMIN_API_PATH },
        { provide: NOTIFICATIONS_ADMIN_API_BASE_URL, useValue: NOTIFICATIONS_ADMIN_API_PATH },
        { provide: SEO_ADMIN_API_BASE_URL, useValue: SEO_ADMIN_API_PATH },
        { provide: CHAT_ADMIN_API_BASE_URL, useValue: CHAT_ADMIN_API_PATH },
        { provide: STATS_ADMIN_API_BASE_URL, useValue: STATS_ADMIN_API_PATH },
        { provide: ANALYTICS_ADMIN_API_BASE_URL, useValue: ANALYTICS_ADMIN_API_PATH },
        { provide: DVS_ADMIN_API_BASE_URL, useValue: DVS_ADMIN_API_PATH },
        { provide: LOCALIZATION_ADMIN_API_BASE_URL, useValue: LOCALIZATION_ADMIN_API_PATH },
        // Shared geo providers (@natkor/ng-shared) backing the address editor. DaData scopes its
        // suggestions to the ACTIVE country per request (the resolver re-reads it, covering runtime
        // country switches); Pelias serves IL only, so its base URL is pinned to the IL API origin.
        { provide: DADATA_API_KEY, useValue: DADATA_SUGGESTIONS_API_KEY },
        {
            provide: DADATA_COUNTRY_SCOPE,
            useFactory: (countryDomain: CountryDomainService) => () => countryDomain.activeCountry,
            deps: [CountryDomainService],
        },
        {
            provide: PELIAS_API_BASE_URL,
            useFactory: (countryDomain: CountryDomainService) => `${countryDomain.apiBaseUrl(CountryCode.IL)}/pelias`,
            deps: [CountryDomainService],
        },
        // Google Places (CIS countries). The loader reads the key once; the country scope is
        // re-resolved per request so a runtime country switch needs no re-wiring.
        { provide: GOOGLE_MAPS_API_KEY, useValue: GOOGLE_PLACES_API_KEY },
        {
            provide: GOOGLE_PLACES_COUNTRY_SCOPE,
            useFactory: (countryDomain: CountryDomainService) => () => countryDomain.activeCountry,
            deps: [CountryDomainService],
        },
    ],
};
